import type { CSSProperties, ReactNode } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

import type { Icon } from '@slite/icons'
import { CheckmarkIcon } from '@slite/icons'

import { Checkbox } from '../Checkbox/Checkbox'
import { Input } from '../Input/Input'
import { Menu } from '../Menu/Menu'
import { MenuHeader } from '../Menu/MenuHeader'
import { MenuItem } from '../Menu/MenuItem'
import { useNavigationListState } from '../NavigationList/hooks/useNavigationListState'
import { NavigationListItem } from '../NavigationList/NavigationListItem'
import { NavigationListProvider } from '../NavigationList/navigationListContext'
import type { PopoverOnClickProps } from '../PopoverOnClick/PopoverOnClick'
import { PopoverOnClick } from '../PopoverOnClick/PopoverOnClick'
import { mergeRefs } from '../utils/mergeRefs'

const SEARCH_ID = '#$%SEARCH%$#'

export type PopoverSelectOption = {
  key: string
  title: ReactNode
  description?: ReactNode
  extraContent?: ReactNode
  disabled?: boolean
  className?: string
  ellipsis?: boolean
  icon?: Icon | ReactNode
  iconClassName?: string
}

export type PopoverSelectProps<T extends PopoverSelectOption> = {
  onSelectOption: (option: T) => void
  options: T[] | ((query: string) => T[])
  target: (
    args: Parameters<PopoverOnClickProps['target']>[0] & {
      value?: T
    }
  ) => React.JSX.Element
  selectedValueKey?: T['key']
  selectedMultiValueKeys?: T['key'][]
  positions?: PopoverOnClickProps['positions']
  align?: PopoverOnClickProps['align']
  menuItemClassName?: string
  menuClassName?: string
  onIsOpenChange?: (isOpen: boolean) => void
  style?: CSSProperties
  maxHeight?: CSSProperties['maxHeight']
  searchOptions?: {
    placeholder?: string
  }
  header?: ReactNode
  disabled?: boolean
}

export function PopoverSelect<T extends PopoverSelectOption>({
  options,
  positions = ['bottom', 'top'],
  align,
  target,
  menuItemClassName,
  menuClassName,
  selectedValueKey,
  selectedMultiValueKeys,
  onSelectOption,
  onIsOpenChange,
  style,
  maxHeight = 280,
  searchOptions,
  header,
  disabled,
}: PopoverSelectProps<T>) {
  const [query, setQuery] = useState('')
  const isMultiSelectMode = selectedMultiValueKeys !== undefined

  const filteredOptions =
    typeof options === 'function' ? options(query) : options

  const idList = filteredOptions
    .filter((option) => !option.disabled)
    .map((option) => option.key)
  if (searchOptions) {
    idList.unshift(SEARCH_ID)
  }

  const navigationList = useNavigationListState({ idList, focusItems: true })

  const isOpenRef = useRef(false)

  // When the list of id changes, re-select the first option
  const memoIdList = idList.join()
  // biome-ignore lint/correctness/useExhaustiveDependencies: memoIdList triggers re-selection when options list changes
  useEffect(() => {
    if (isOpenRef.current) {
      navigationList.selectFirst()
    }
  }, [navigationList, memoIdList])

  const onOpenCalledRef = useRef(false)

  const selectedOption = filteredOptions.find(
    (option) => option.key === selectedValueKey
  )

  return (
    <NavigationListProvider value={navigationList}>
      <PopoverOnClick
        content={({ ref, close }) => (
          <Menu
            ref={mergeRefs(ref, () => {
              // Select first when the menu mounts in the DOM so the first
              // item gets the focus for keyboard navigation.
              if (onOpenCalledRef.current) {
                return
              }
              if (selectedValueKey) {
                navigationList.select(selectedValueKey)
              } else {
                navigationList.selectFirst()
              }
              onOpenCalledRef.current = true
            })}
            className={classNames(menuClassName, 'scrollable shadow-on-scroll')}
            style={{
              ...style,
              maxHeight,
              paddingTop: searchOptions ? 0 : undefined,
            }}
            onRowClick={isMultiSelectMode ? () => {} : close}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault()
              } else if (e.key === 'Escape') {
                close()
              } else if (e.key === 'Enter') {
                const selectedOption = filteredOptions.find(
                  (option) => option.key === navigationList.getSelectedId()
                )
                if (selectedOption) {
                  e.preventDefault()
                  onSelectOption(selectedOption)
                  close()
                }
              } else {
                navigationList.select(SEARCH_ID)
              }
            }}
          >
            {header && <MenuHeader>{header}</MenuHeader>}
            {searchOptions && (
              <NavigationListItem<HTMLInputElement> id={SEARCH_ID}>
                {({ onKeyDown }, ref) => (
                  <div className="position-sticky padding-horizontal-4 padding-top-4 padding-bottom-2 background-shade-100 top-0">
                    <Input
                      ref={ref}
                      onKeyDown={onKeyDown}
                      value={query}
                      onChange={(event) => setQuery(event.currentTarget.value)}
                      placeholder={searchOptions?.placeholder}
                      autoFocus
                      onFocus={() => navigationList.select(SEARCH_ID)}
                    />
                  </div>
                )}
              </NavigationListItem>
            )}
            {filteredOptions.map((option) => (
              <NavigationListItem<HTMLButtonElement>
                id={option.key}
                key={option.key}
                selectOnHover
              >
                {({ isSelected, onKeyDown, onMouseEnter }, ref) => (
                  <MenuItem
                    id={option.key}
                    ref={ref}
                    className={classNames(menuItemClassName, option.className)}
                    icon={option.icon}
                    iconClassName={option.iconClassName}
                    isActive={isSelected}
                    isSelected={selectedValueKey === option.key}
                    disabled={option.disabled}
                    onClick={() => onSelectOption(option)}
                    data-test-id="menuItem"
                    onKeyDown={onKeyDown}
                    onMouseEnter={onMouseEnter}
                  >
                    <div className="flexbox flexbox-column width-full text-align-left">
                      <div className="flexbox flexbox-align-center">
                        <div
                          className={classNames(
                            'shade-05',
                            {
                              ellipsis: option.ellipsis,
                              'margin-bottom-4': option.description,
                              'font-weight-semibold':
                                option.title && option.description,
                            },
                            option.className
                          )}
                        >
                          {option.title}
                        </div>
                        {option.extraContent}
                      </div>
                      {option.description && (
                        <div
                          className={classNames(
                            'font-weight-regular shade-20 text-align-left',
                            option.ellipsis && 'ellipsis'
                          )}
                        >
                          {option.description}
                        </div>
                      )}
                    </div>
                    {isMultiSelectMode ? (
                      <Checkbox
                        className="margin-left-4"
                        checked={selectedMultiValueKeys.includes(option.key)}
                      />
                    ) : (
                      selectedValueKey === option.key && (
                        <CheckmarkIcon className="margin-left-4" size={16} />
                      )
                    )}
                  </MenuItem>
                )}
              </NavigationListItem>
            ))}
          </Menu>
        )}
        positions={positions}
        align={align}
        onIsOpenChange={(isOpen) => {
          isOpenRef.current = isOpen
          if (!isOpen) {
            onOpenCalledRef.current = false
            navigationList.deselect()
            setQuery('')
          }
          onIsOpenChange?.(isOpen)
        }}
        target={(args) => target({ ...args, value: selectedOption })}
        disabled={disabled}
      />
    </NavigationListProvider>
  )
}
